import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import ProvisioningStyles from 'pages/cloud-onboarding/style';
import { orderId, orderPayload, toggleSspModels, isValidOwners, uploadedFilesState } from 'pages/provisioning/aws/cloud-onboarding/store';
import { formOrderObjFromOrderDetails, resetToggleSspModal, resetOrder, formUploadObjFromOrderDetails } from 'pages/provisioning/aws/cloud-onboarding/utils';
import AwsPrivilegedADGroupModal from 'pages/provisioning/aws/cloud-onboarding/components/Form/AccessManagementSection/AwsPrivilegedADGroupModal';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aws/cloud-onboarding/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/provisioning/aws/cloud-onboarding/SspForm'));
const OrderSuccessDialog = React.lazy(() => import('pages/provisioning/aws/cloud-onboarding/OrderSuccessDialog'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aws/cloud-onboarding/ShowCostDetails'));

const AWSCloudOnboardingProvisioning = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [order, setOrder] = useRecoilState(orderPayload);
    const [, setUploadedFiles] = useRecoilState(uploadedFilesState);
    const resetUploadFiles = useResetRecoilState(uploadedFilesState);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetValidOwners] = useRecoilState(isValidOwners);

    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    const bAppPostApi = useMutation((payload) => axios.post(endPoints.riServices.COMMON.businessCiDetails, payload));

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetailsV2}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
            let bAppResponse = null;
            if (orderObj?.businessApp) {
                bAppResponse = await bAppPostApi?.mutateAsync({
                    businessApplication: orderObj?.businessApp,
                    applicationService: null,
                });
            }
            setUploadedFiles(uploadObj);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                businessAppDetails: bAppResponse?.data?.applications?.[0],
                emptyCost: costs,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetToggleSspModels(resetToggleSspModal);
        resetValidOwners({
            isvalidAppOwner: true,
        });
        resetUploadFiles();
    };

    React.useEffect(() => {
        if (catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [catalogCode]);

    React.useEffect(() => {
        return () => reset();
    }, []);
    return (
        <>
            <Helmet>
                <title>AWS Cloud Onboarding</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} data-testid="index-form" className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <OrderSuccessDialog />
                        <ShowCostDetails />
                        <AwsPrivilegedADGroupModal />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default AWSCloudOnboardingProvisioning;
